import { action, asyncAction } from '@play-co/replicant';
import createActions from '../config/actions/createActions';
import {
  weeklyLeaderboardRewards,
  weeklyLeaderboardSchedule,
  weeklyLeaderboardSize,
  weeklyLeaderboardUnlockLevel,
} from './weeklyLeaderboard.ruleset';
import {
  getOngoingWeeklyLeaderboardEvent,
  getWeeklyLeaderboardCommonEventProperties,
  getWeeklyLeaderboardFriendsIDs,
  getWeeklyLeaderboardRankingArgs,
} from './weeklyLeaderboard.getters';
import { getPuzzleWinsLast7Days } from '../puzzleWinCount/puzzleWinCount.getters';
import { canPlayLeague } from '../match2/leagues/leagues.getters';
import { getMatch2MaxLevel } from '../match2/match2.getters';
import { grantMatch2Rewards } from '../match2/match2.modifiers';
import { Match2Reward } from '../match2/rewards/match2-rewards.ruleset';
import {
  updateWeeklyLeaderboardLastSeen,
  updateWeeklyLeaderboardPlayerRank,
} from './weeklyLeaderboard.modifiers';
import { sendAnalyticsEvents } from '../analytics';
import { isDynamicTestInBucket } from '../getters/ab';
import ab from '../ruleset/ab';
import { ElasticSearchQueryWhere } from '../config/asyncGetters/createAsyncGetters';

export const leaderboardsActions = createActions({
  /**
   * Run the matchmaking process for the user, appending opponents to the user entry
   */
  asyncWeeklyLeaderboardMatchmaking: asyncAction(
    async (state, args: { eventId?: string } = {}, api) => {
      if (!state.weeklyLeaderboard) return;

      const event = args.eventId
        ? weeklyLeaderboardSchedule.findEventByID(args.eventId)
        : getOngoingWeeklyLeaderboardEvent(state, api.date.now());
      if (!event) return;

      const userEntry = state.weeklyLeaderboard.entries[event.id];
      if (!userEntry) return;

      // Check if this the first matchmaking for the user
      const firstMatchmaking = userEntry.opponents.length === 0;

      // Maximum number of opponents to find, excluding the user
      const maxOpponents = weeklyLeaderboardSize - 1;

      // How many opponents can still be added
      const openSpots = maxOpponents - userEntry.opponents.length;

      // Do nothing if the opponents list is already full
      if (openSpots <= 0) return;

      // User's number of wins in the last 7 days
      const userWinsLast7Days = getPuzzleWinsLast7Days(
        state,
        api.date.now(),
      ).reduce((acc, val) => acc + val, 0);

      // Base wins is 20% of the last 7 days wins, rounded up
      const baseUserWins = Math.ceil(Math.max(1, userWinsLast7Days) * 1.2);

      // User's friends ids
      const friends = getWeeklyLeaderboardFriendsIDs(state);

      // Search opponents by wins
      const opponents = await api.searchPlayers({
        scoring: {
          functions: {
            puzzleWinsLast7Days: {
              // The base number of wins is used as the origin, cannot be 0
              origin: baseUserWins,
              // Gauss decreases smoothly as we move away from the origin
              function: 'gauss',
              // The scale is ±10% of the base number of wins, minimum 1
              scale: Math.ceil(baseUserWins * 0.1),
              // The decay is 50% of the scale
              decay: 0.5,
            },
            match2Level: {
              // The origin is the user's current level
              origin: state.match2.level,
              // Gauss decreases smoothly as we move away from the origin
              function: 'gauss',
              // The scale is ±50 of the user's current level
              scale: 50,
              // The decay is 50% of the scale
              decay: 0.5,
            },
          },
          should: {
            // It should prefer friends, but not exclusively
            id: { isOneOf: [...friends] },
            // It should split players with the same league status
            match2Level: {
              [canPlayLeague(state) ? 'greaterThan' : 'lessThanOrEqual']:
                getMatch2MaxLevel(state),
            },
          },
          mode: 'multiply',
        },
        where: {
          match2Level: {
            // The opponents should be at least the weekly leaderboard unlock level
            greaterThanOrEqual: weeklyLeaderboardUnlockLevel,
          },
          id: {
            // Should not be the user or any of the current opponents
            isNotOneOf: [state.id, ...userEntry.opponents],
          },
          // Should have scored at least 1 point in same event
          weeklyLeaderboardEntries: {
            eventId: { isAnyOf: [event.id] },
            score: { greaterThanOrEqual: 1 },
          },
        },
        // Limit the search to the number of open spots in the opponents list
        limit: openSpots,
      });

      const finalList = opponents.results.map((opponent) => opponent.id);

      if (finalList.length) {
        // Update the user entry
        userEntry.opponents.push(...finalList);
      }

      if (firstMatchmaking) {
        // If first matchmaking, update the player rank and send analytics event
        await updateWeeklyLeaderboardPlayerRank(state, api);
        sendAnalyticsEvents(api, [
          {
            eventType: 'WeeklyLeaderboardInitialize',
            eventProperties: {
              ...getWeeklyLeaderboardCommonEventProperties(
                state,
                event.id,
                api.date.now(),
              ),
            },
          },
        ]);
      }
    },
  ),

  /**
   * Claim all past leaderboards that were not claimed yet.
   * @returns List of all rewards claimed
   */
  asyncClaimWeeklyLeaderboards: asyncAction(async (state, _, api) => {
    if (!state.weeklyLeaderboard) return undefined;

    // Find out current event - user can still claim if there is none
    const currentEvent = getOngoingWeeklyLeaderboardEvent(
      state,
      api.date.now(),
    );

    const allRewards: Match2Reward[] = [];

    // This action can claim multiple events, but we return only the best tier (0=gold, 1=silver, 2=bronze)
    let rewardsTier = 2;

    // Find out the current event and skip if there is none
    for (const [eventId, entry] of Object.entries(
      state.weeklyLeaderboard.entries,
    )) {
      // Skip current event
      if (eventId === currentEvent?.id) {
        continue;
      }

      // Skip already claimed leaderboards
      if (entry.claimedAt) {
        continue;
      }

      // If entry had no opponents, matchmaking never happened for that event
      // So just close the leaderboard and don't add any rewards
      if (entry.opponents.length === 0) {
        entry.claimedAt = api.date.now();
        continue;
      }

      // Fetch the leaderboard full data
      const rankingArgs = getWeeklyLeaderboardRankingArgs(state, eventId);
      if (!rankingArgs) {
        continue;
      }

      const ranking =
        await api.asyncGetters.getWeeklyLeaderboardRanking(rankingArgs);

      const finalPosition = ranking.findIndex((entry) => entry.id === state.id);
      const rewards = weeklyLeaderboardRewards[finalPosition];

      // Claim the leaderboard rewards if any
      if (rewards) {
        // Update the overall rewards tier
        if (finalPosition < rewardsTier) {
          rewardsTier = finalPosition;
        }

        allRewards.push(...rewards);
        grantMatch2Rewards(
          state,
          rewards,
          {
            feature: 'weekly_leaderboard',
            $subFeature: eventId,
          },
          api,
        );
      }

      // Mark leaderboard as claimed
      entry.position = finalPosition + 1; // 1-based index
      entry.claimedAt = api.date.now();

      // Send analytics event for the finalized leaderboard
      sendAnalyticsEvents(api, [
        {
          eventType: 'WeeklyLeaderboardFinalize',
          eventProperties: {
            ...getWeeklyLeaderboardCommonEventProperties(
              state,
              eventId,
              api.date.now(),
            ),
            claimedRewards: rewards && rewards.length > 0,
          },
        },
      ]);
    }

    // Return all rewards claimed
    return {
      tier: rewardsTier,
      rewards: allRewards,
    };
  }),

  /**
   * Update the last seen timestamp of the Weekly Leaderboard
   */
  updateWeeklyLeaderboardLastSeen: action((state, _, api) => {
    // Update the last seen timestamp
    updateWeeklyLeaderboardLastSeen(state, api.date.now());
  }),
});
